// Anonymous device and visit identifiers, sent on every API request.
//
// Separate from the member session token: `anonId` survives sign-out so a
// returning traveller stays recognisable, and both exist for travellers who
// never sign in at all.

const ANON_KEY = 'suitescanner-anon-id';
const SESSION_KEY = 'suitescanner-session';

// A visit ends after this much inactivity. The next request starts a new one.
const SESSION_IDLE_MS = 30 * 60 * 1000;

// Persisting `lastSeen` on every call would write on every keystroke through
// destination autocomplete, so it is only flushed this often.
const SESSION_FLUSH_MS = 60 * 1000;

type WebStorage = {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
};

type Session = {
    id: string;
    lastSeen: number;
};

let anonId: string | null = null;
let session: Session | null = null;
let flushedAt = 0;

// Safari private browsing exposes both storages but throws on write, and the
// Expo web build also runs these modules during static export where neither
// exists. Identity falls back to memory rather than taking the app down.
function storage(kind: 'local' | 'session'): WebStorage | null {
    try {
        const global = globalThis as { localStorage?: WebStorage; sessionStorage?: WebStorage };

        return (kind === 'local' ? global.localStorage : global.sessionStorage) ?? null;
    } catch {
        return null;
    }
}

function read(kind: 'local' | 'session', key: string): string | null {
    try {
        return storage(kind)?.getItem(key) ?? null;
    } catch {
        return null;
    }
}

function write(kind: 'local' | 'session', key: string, value: string) {
    try {
        storage(kind)?.setItem(key, value);
    } catch {
        // Quota or private-mode refusal. The in-memory value still serves this visit.
    }
}

function uuid(): string {
    const cryptoApi = globalThis.crypto;

    // randomUUID needs a secure context, which excludes a plain-http LAN address
    // during development.
    if (typeof cryptoApi?.randomUUID === 'function') {
        return cryptoApi.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
        const random = Math.random() * 16 | 0;
        const value = char === 'x' ? random : (random & 0x3 | 0x8);

        return value.toString(16);
    });
}

function getAnonId(): string {
    if (anonId) return anonId;

    anonId = read('local', ANON_KEY) ?? uuid();
    write('local', ANON_KEY, anonId);

    return anonId;
}

function restoreSession(): Session | null {
    const raw = read('session', SESSION_KEY);
    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw) as Partial<Session>;

        return typeof parsed.id === 'string' && typeof parsed.lastSeen === 'number'
            ? { id: parsed.id, lastSeen: parsed.lastSeen }
            : null;
    } catch {
        return null;
    }
}

function getSessionId(): string {
    const now = Date.now();
    const current = session ?? restoreSession();

    if (current && now - current.lastSeen < SESSION_IDLE_MS) {
        session = { id: current.id, lastSeen: now };
    } else {
        session = { id: uuid(), lastSeen: now };
        flushedAt = 0;
    }

    if (now - flushedAt > SESSION_FLUSH_MS) {
        write('session', SESSION_KEY, JSON.stringify(session));
        flushedAt = now;
    }

    return session.id;
}

export { getAnonId, getSessionId };
